import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Send, Loader2, Sparkles } from "lucide-react";
import ReactMarkdown from "react-markdown";
import { useTranslation } from "react-i18next";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function ChatBubble({ msg }) {
  const { t } = useTranslation();
  const isUser = msg.role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${
        isUser ? "bg-[#0B1F3B] text-white rounded-br-md" : "bg-white border border-slate-200 text-slate-700 rounded-bl-md"
      }`}>
        {!isUser && (
          <div className="flex items-center gap-1.5 mb-1.5">
            <Sparkles className="w-3.5 h-3.5 text-[#D4AF37]" />
            <span className="text-xs font-semibold text-[#D4AF37]">{t("advisor.projectAdvisor")}</span>
          </div>
        )}
        <div className="advisor-markdown prose prose-sm max-w-none"><ReactMarkdown>{msg.content}</ReactMarkdown></div>
      </div>
    </div>
  );
}

export default function AdvisorChat() {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language?.startsWith("es") ? "es" : "en";
  const [messages, setMessages] = useState([
    { role: "assistant", content: t("advisor.greeting") }
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const navigate = useNavigate();

  const scrollToBottom = () => messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });

  const handleSend = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;
    const userMsg = input.trim();
    setInput("");
    const newMessages = [...messages, { role: "user", content: userMsg }];
    setMessages(newMessages);
    setLoading(true);
    setTimeout(scrollToBottom, 100);

    try {
      const history = newMessages.map(m => ({ role: m.role, content: m.content }));
      const res = await fetch(`${API}/advisor/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: userMsg, history, lang: currentLang }),
      });

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let assistantText = "";
      setMessages(prev => [...prev, { role: "assistant", content: "" }]);

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value);
        const parts = chunk.split("\n");
        for (let i = 0; i < parts.length; i++) {
          if (!parts[i].startsWith("data: ")) continue;
          const data = parts[i].slice(6);
          if (data === "[DONE]") break;
          try { assistantText += JSON.parse(data); } catch { assistantText += data; }
        }
        setMessages(prev => {
          const updated = prev.slice();
          updated[updated.length - 1] = { role: "assistant", content: assistantText };
          return updated;
        });
        setTimeout(scrollToBottom, 50);
      }
    } catch {
      setMessages(prev => [...prev, { role: "assistant", content: t("advisor.connectionError") }]);
    } finally {
      setLoading(false);
      setTimeout(scrollToBottom, 100);
    }
  };

  return (
    <div className="bg-white rounded-2xl border-2 border-slate-200 overflow-hidden shadow-lg" data-testid="advisor-chat">
      <div className="h-[360px] overflow-y-auto p-5 space-y-4 bg-[#F8FAFC]">
        {messages.map((msg, i) => (
          <ChatBubble key={`msg-${i}-${msg.role}`} msg={msg} />
        ))}
        {loading && messages[messages.length - 1]?.role === "user" && (
          <div className="flex justify-start">
            <div className="bg-white border border-slate-200 rounded-2xl rounded-bl-md px-4 py-3">
              <Loader2 className="w-4 h-4 animate-spin text-[#D4AF37]" />
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>
      <form onSubmit={handleSend} className="border-t border-slate-200 p-4 flex gap-3 bg-white">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={t("advisor.inputPlaceholder")}
          className="flex-1 h-11 rounded-lg border border-slate-200 px-4 text-sm focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/50 focus:border-[#D4AF37]"
          disabled={loading}
          data-testid="advisor-input"
        />
        <Button type="submit" disabled={loading || !input.trim()} className="bg-[#0B1F3B] hover:bg-[#162A4C] text-white h-11 px-5" data-testid="advisor-send">
          <Send className="w-4 h-4" />
        </Button>
      </form>
      <div className="bg-[#0B1F3B]/5 border-t border-slate-200 px-5 py-3 flex items-center justify-between">
        <p className="text-xs text-slate-500">{t("advisor.readyForBids")}</p>
        <Button size="sm" onClick={() => navigate("/register")} className="bg-[#D4AF37] hover:bg-[#C4A030] text-[#0B1F3B] font-bold text-xs" data-testid="advisor-cta">
          {t("advisor.postYourProject")}
        </Button>
      </div>
    </div>
  );
}
